import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { t } from "@/lib/i18n/interpolate";
import {
  type BondEnthalpy,
  type BondEntry,
  heatKind,
  KJ_PER_KCAL,
} from "@/lib/tool-kit/chem-reference/bond-enthalpy";

export interface BondResultStrings {
  deltaH: string;
  brokenTotal: string;
  formedTotal: string;
  heat: string;
  heatNames: { exothermic: string; endothermic: string; none: string };
  unitNames: { kJ: string; kcal: string };
  brokenHeading: string;
  formedHeading: string;
  columnBond: string;
  columnCount: string;
  columnEnergy: string;
  columnSubtotal: string;
  customNote: string;
  working: string;
  workBroken: string;
  workFormed: string;
  workDelta: string;
  estimateNote: string;
  copy: string;
  copied: string;
  copyText: string;
}

export type EnergyUnit = "kJ" | "kcal";

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const ONE_DECIMAL = { maximumFractionDigits: 1, useGrouping: false } as const;

function inUnit(kj: number, unit: EnergyUnit): number {
  return unit === "kcal" ? kj / KJ_PER_KCAL : kj;
}

function SideTable({
  heading,
  entries,
  strings,
  fmt,
}: {
  heading: string;
  entries: readonly BondEntry[];
  strings: BondResultStrings;
  fmt: Fmt;
}) {
  if (entries.length === 0) {
    return null;
  }
  const rows = entries.map((entry) => [
    entry.bond.label,
    fmt(entry.count, ONE_DECIMAL),
    `${fmt(entry.energy, ONE_DECIMAL)}${entry.overridden ? " *" : ""}`,
    fmt(entry.count * entry.energy, ONE_DECIMAL),
  ]);
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{heading}</h3>
      <DataTable
        head={[
          strings.columnBond,
          strings.columnCount,
          strings.columnEnergy,
          strings.columnSubtotal,
        ]}
        rows={rows}
      />
    </div>
  );
}

export function BondResults({
  result,
  broken,
  formed,
  unit,
  strings,
  fmt,
}: {
  result: BondEnthalpy;
  broken: readonly BondEntry[];
  formed: readonly BondEntry[];
  unit: EnergyUnit;
  strings: BondResultStrings;
  fmt: Fmt;
}) {
  const unitName = strings.unitNames[unit];
  const show = (kj: number) => fmt(inUnit(kj, unit), ONE_DECIMAL);
  const delta = `${show(result.delta)} ${unitName}`;
  const custom = [...broken, ...formed].some((entry) => entry.overridden);
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.deltaH} value={delta} />
        <StatCard
          label={strings.heat}
          value={strings.heatNames[heatKind(result.delta)]}
        />
        <StatCard
          label={strings.brokenTotal}
          value={`${show(result.broken)} ${unitName}`}
        />
        <StatCard
          label={strings.formedTotal}
          value={`${show(result.formed)} ${unitName}`}
        />
      </StatGrid>
      <SideTable
        entries={broken}
        fmt={fmt}
        heading={strings.brokenHeading}
        strings={strings}
      />
      <SideTable
        entries={formed}
        fmt={fmt}
        heading={strings.formedHeading}
        strings={strings}
      />
      {custom ? <Notice>{strings.customNote}</Notice> : null}
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>
          {t(strings.workBroken, {
            sum:
              broken
                .map(
                  (e) =>
                    `${fmt(e.count, ONE_DECIMAL)} × ${fmt(e.energy, ONE_DECIMAL)}`
                )
                .join(" + ") || "0",
            total: show(result.broken),
          })}
        </p>
        <p>
          {t(strings.workFormed, {
            sum:
              formed
                .map(
                  (e) =>
                    `${fmt(e.count, ONE_DECIMAL)} × ${fmt(e.energy, ONE_DECIMAL)}`
                )
                .join(" + ") || "0",
            total: show(result.formed),
          })}
        </p>
        <p>
          {t(strings.workDelta, {
            broken: show(result.broken),
            formed: show(result.formed),
            delta,
          })}
        </p>
      </div>
      <Notice>{strings.estimateNote}</Notice>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          delta,
          heat: strings.heatNames[heatKind(result.delta)],
        })}
      />
    </>
  );
}
